import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { createRelayResource, useRelayResource } from '@townsquare/relay-utils';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { RouteResource } from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import ActivationResourceQuery, {
	type activationResourceQuery as ActivationResourceQueryType,
} from './__generated__/activationResourceQuery.graphql';

export const rovoActivationResource: RouteResource<
	PreloadedQuery<ActivationResourceQueryType, Record<string, unknown>>
> = createRelayResource<ActivationResourceQueryType>({
	getQuery: (_routerContext, resourceContext) => {
		const { collabContext } = resourceContext as ResourceContext;

		const cloudId = collabContext?.rovoWorkspace;

		return {
			query: ActivationResourceQuery,
			environment: getAggRelayEnvironment(),
			variables: {
				// If cloudId is falsy then isEnabled will be false and the query will skip anyway
				cloudId: cloudId ?? '',
				isEnabled: !!cloudId && fg('townsquare_agents_list') && fg('rovo-chat-ai-inbox'),
			},
		};
	},
});

export const useRovoActivationIdQueryRef = (): RovoActivationIdQueryRef | undefined => {
	return useRelayResource(rovoActivationResource) ?? undefined;
};

export type RovoActivationIdQueryRef = PreloadedQuery<ActivationResourceQueryType>;

type RovoActivationIdExtractorProps = {
	children: (rovoActivationId: string | null | undefined) => React.JSX.Element | null;
	queryRef: RovoActivationIdQueryRef;
};

// Hooks for checking preloaded queries are not allowed, so to extract data from this resource without exposing the query everywhere we must have a HOC.
export const RovoActivationIdExtractor = ({
	children,
	queryRef,
}: RovoActivationIdExtractorProps): React.JSX.Element | null => {
	const data = usePreloadedQuery(
		graphql`
			query activationResourceQuery($cloudId: ID!, $isEnabled: Boolean!) {
				tenantContexts(cloudIds: [$cloudId]) @include(if: $isEnabled) {
					activationIdByProduct(product: "rovo") {
						active
					}
				}
			}
		`,
		queryRef,
	);

	const activationId = data?.tenantContexts?.[0]?.activationIdByProduct?.active;

	return children(activationId);
};
